import { Check, Pencil, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import { HelpTip } from '../../../../components/HelpTip';
import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import { TdInput, TdNumber, TdSelect } from '../../../../components/inline-add-table/cells';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import type { SelectOption } from '../../../../components/inputs/selectShared';
import { Textarea } from '../../../../components/inputs/Textarea';
import { formatMoney } from '../../../../lib/formatMoney';
import type { EligibilityOption } from '../../eligibilities/eligibilityTypes';
import { salaryTypeLabel, type SalaryGradeOption } from '../../plantilla/plantillaTypes';
import type { PositionListItem } from '../positionTypes';
import {
  blankEligibilityRow,
  blankTextRow,
  blankTrainingRow,
  codeIsValid,
  gradeLabel,
  gradeOptionsFor,
  NO_GRADE,
  STAGED_ELIGIBILITY,
  stagedEligibilityName,
  trainingRowInvalid,
  type PositionDraft,
  type TextRow,
} from '../utils/positionDraft';

/** The cap the server holds on lines per standard — stated where the reader meets it. */
const LINES_MAX = 50;

/**
 * The fields of one catalog title, qualification standards included.
 *
 * Drawn by the Positions modal and by the plantilla form's "Create new position" step, so a
 * title added from either asks the same questions in the same order. The caller owns the
 * draft, the pickers' options and the save; this owns only the fields.
 *
 * Ids are `${idPrefix}-<field>`, and an inline-table cell is `${idPrefix}-<list>[<row>]` (a
 * training cell adds `.text` / `.hours`) — the server's own spelling of a row, so a refused
 * line is focused by `focusValidationError` with no mapping. A refusal of a whole LIST
 * (`education`) is mapped by the caller to its first row.
 */
export function PositionFields({
  idPrefix,
  draft,
  onChange,
  editing = null,
  grades,
  eligibilities,
  loadingOptions,
  disabled,
}: {
  idPrefix: string;
  draft: PositionDraft;
  onChange: (patch: Partial<PositionDraft>) => void;
  /** The title being edited, so a grade or eligibility it records that has since gone stays selectable. */
  editing?: PositionListItem | null;
  grades: SalaryGradeOption[];
  eligibilities: EligibilityOption[];
  loadingOptions: boolean;
  disabled: boolean;
}) {
  /*
    Adding an eligibility from the picker writes to ANOTHER catalog, so it carries that
    catalog's own create key — and the sentence offering it drops with the gate.
  */
  const { hasPermission } = useAuth();
  const canCreateEligibility = hasPermission('eligibilities.create');

  /*
    A grade the schedule no longer offers stays selectable where it already was — the title
    still records it, and a picker that dropped it would read as blank on a row that has one.
  */
  const gradeRows = gradeOptionsFor(grades);

  // What each grade pays, for the CLOSED picker: "SG 12 - Step 4 · 25,439.00" (19 Oct 2026).
  const amountByGrade = new Map(gradeRows.map((row) => [row.value, row.amount]));

  const gradeOptions: SelectOption[] = gradeRows.map(({ amount, ...option }) => ({
    ...option,
    // An amount: formatted in one place, mono and tabular — `formatMoney` says "—" for none.
    right: <span className="font-mono tabular-nums">{formatMoney(amount)}</span>,
  }));

  if (
    editing &&
    draft.gradeKey !== NO_GRADE &&
    !gradeOptions.some((option) => option.value === draft.gradeKey)
  ) {
    gradeOptions.push({
      value: draft.gradeKey,
      label: gradeLabel(editing.salaryGrade, editing.salaryStep),
      sublabel: `${salaryTypeLabel(editing.salaryGradeType)} · no longer listed`,
    });
  }

  /*
    The grade is required and its only source is the published schedule, so an empty schedule
    means no title can be created at all. The form says that outright rather than showing a
    required picker with nothing in it.
  */
  const scheduleEmpty = !loadingOptions && gradeOptions.length === 0;

  /*
    The catalog's live eligibilities, plus — on an edit — any the title already accepts that
    the catalog has since deleted. Those stay selectable and SAY so rather than vanishing and
    leaving a row looking empty; the server leaves an accepted one alone and refuses a
    deleted one on a fresh write, which is what makes saying so honest.
  */
  const eligibilityOptions = eligibilities.map((e) => ({ value: e.id, label: e.name }));

  for (const recorded of editing?.eligibilities ?? []) {
    if (!eligibilityOptions.some((option) => option.value === recorded.id)) {
      eligibilityOptions.push({ value: recorded.id, label: `${recorded.name} · deleted` });
    }
  }

  // A STAGED eligibility is offered under its typed name, unmarked (19 Oct 2026), until the save writes it.
  for (const row of draft.eligibilities) {
    const staged = stagedEligibilityName(row.eligibilityId);

    if (staged !== null && !eligibilityOptions.some((option) => option.value === row.eligibilityId)) {
      eligibilityOptions.push({ value: row.eligibilityId!, label: staged });
    }
  }

  // The row whose TYPED eligibility is being renamed in place, if any.
  const [editingKey, setEditingKey] = useState<string | null>(null);

  /**
   * STAGES an eligibility typed into a row's picker — written only if the form saves (see
   * `resolveStagedEligibilities`). The row carries the name verbatim behind a sentinel; picking
   * a real row replaces it.
   */
  function stageEligibility(rowKey: string, name: string) {
    const trimmed = name.trim();
    if (trimmed.length === 0) return;

    onChange({
      eligibilities: draft.eligibilities.map((row) =>
        row.key === rowKey ? { ...row, eligibilityId: `${STAGED_ELIGIBILITY}${trimmed}` } : row,
      ),
    });
  }

  return (
    <>
      <TextField
        id={`${idPrefix}-title`}
        label="Title"
        required
        description="The designation, e.g. Administrative Officer II. Held once in the catalog, however many posts carry it."
        value={draft.title}
        onChange={(e) => onChange({ title: e.target.value })}
        maxLength={150}
        disabled={disabled}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          id={`${idPrefix}-code`}
          label="Code"
          required
          /*
            Editable on an edit, unlike a post's code — nothing is named after this one. A
            post numbers its slots from its OWN code and points at this row by id, so
            correcting a typo here strands nothing.
          */
          description="A short identifier for the title, e.g. ADOF2. Nothing is numbered from it, so it can be corrected later."
          value={draft.code}
          onChange={(e) => onChange({ code: e.target.value })}
          maxLength={20}
          error={draft.code === '' || codeIsValid(draft.code) ? undefined : 'Letters, numbers and hyphens only.'}
          disabled={disabled}
        />

        <Select
          id={`${idPrefix}-grade`}
          label="Salary grade"
          required
          remote
          loading={loadingOptions}
          clearable={false}
          description="The grade this title is authorized at. Managed under Salary Grades, and every post drawn from this title reads its grade from here."
          value={draft.gradeKey}
          onChange={(next) => onChange({ gradeKey: next ?? NO_GRADE })}
          /*
            No "None" option: the grade is required, so offering one would put a choice on the
            form that the primary action then refuses. The placeholder value is unselectable —
            it only ever holds the pre-choice state.
          */
          options={gradeOptions}
          // Already in grade order — the default label sort would put SG 30 before SG 4.
          sort="none"
          displayLabel={(option) => {
            const amount = amountByGrade.get(option.value);
            return amount == null ? option.label : `${option.label} · ${formatMoney(amount)}`;
          }}
          error={
            scheduleEmpty
              ? 'No salary grade has been published yet. Add one under Salary Grades first.'
              : draft.gradeKey === NO_GRADE
                ? 'Choose the grade this title is authorized at.'
                : undefined
          }
          disabled={disabled || scheduleEmpty}
        />
      </div>

      <TextField
        id={`${idPrefix}-description`}
        label="Description"
        description="Optional note shown on the position."
        value={draft.description}
        onChange={(e) => onChange({ description: e.target.value })}
        maxLength={500}
        disabled={disabled}
      />

      {/*
        The qualification standards, each a LIST entered as rows of an inline table (product
        call, 16 Oct 2026). All four required; other expectations is optional.
      */}
      <div className="border-t border-slate-200 pt-4">
        <h3 className="text-sm text-slate-800">Qualification standards</h3>
        <p className="mt-0.5 text-xs text-slate-500">
          What this title demands of whoever holds it. Every post drawn from this title
          inherits all of them.
        </p>
      </div>

      {/*
        THE ELIGIBILITY — OPTIONAL (16 Oct 2026): the eligibilities a title accepts, any ONE of
        which qualifies, or none listed when it requires none. A row's kebab holds Delete, and
        Edit for an eligibility typed here that is not written yet — renaming it in place; an
        existing catalog row is swapped or removed, never renamed from this form.
      */}
      <QualificationLabel
        label="Eligibility"
        required={false}
        help="The eligibilities this title accepts — any one qualifies. Leave it empty when the title requires none."
      >
        <InlineAddTable
          headers={['Eligibility']}
          colWidths={[520]}
          flexColumn={0}
          rowCount={draft.eligibilities.length}
          onAddRow={() => onChange({ eligibilities: [...draft.eligibilities, blankEligibilityRow()] })}
          addDisabled={
            disabled ||
            draft.eligibilities.some(
              (row) => row.eligibilityId === null || stagedEligibilityName(row.eligibilityId)?.trim() === '',
            ) ||
            draft.eligibilities.length >= LINES_MAX
          }
          addDisabledReason={
            draft.eligibilities.length >= LINES_MAX
              ? `A title may accept at most ${LINES_MAX} eligibilities.`
              : 'Choose an eligibility in the current row before adding another.'
          }
          addButtonId={`${idPrefix}-eligibility-ids-add`}
        >
          {draft.eligibilities.map((row, index) => {
            // A row offers what no OTHER row has chosen — the same eligibility twice is one.
            const taken = new Set(
              draft.eligibilities.filter((other) => other.key !== row.key).map((other) => other.eligibilityId),
            );
            const staged = stagedEligibilityName(row.eligibilityId);
            const editingThis = editingKey === row.key && staged !== null;

            const setRow = (eligibilityId: string | null) =>
              onChange({
                eligibilities: draft.eligibilities.map((other) =>
                  other.key === row.key ? { ...other, eligibilityId } : other,
                ),
              });

            const remove = () => {
              const next = draft.eligibilities.filter((other) => other.key !== row.key);
              if (editingKey === row.key) setEditingKey(null);
              onChange({ eligibilities: next.length > 0 ? next : [blankEligibilityRow()] });
            };

            return (
              <InlineRow
                key={row.key}
                idx={index + 1}
                onRemove={remove}
                menu={{
                  id: `${idPrefix}-eligibility-ids[${index}]-menu`,
                  items: [
                    // Edit renames a TYPED eligibility only — one not written to the catalog yet.
                    ...(staged !== null
                      ? [
                          editingThis
                            ? { key: 'done', label: 'Done editing', icon: <Check className="h-4 w-4" />, onClick: () => setEditingKey(null) }
                            : { key: 'edit', label: 'Edit', icon: <Pencil className="h-4 w-4" />, onClick: () => setEditingKey(row.key) },
                        ]
                      : []),
                    {
                      key: 'delete',
                      label: 'Delete',
                      icon: <Trash2 className="h-4 w-4" />,
                      destructive: true,
                      onClick: remove,
                    },
                  ],
                }}
              >
                {editingThis ? (
                  <TdInput
                    id={`${idPrefix}-eligibility-ids[${index}]`}
                    ariaLabel={`Eligibility name, row ${index + 1}`}
                    placeholder="The eligibility's name"
                    value={staged}
                    onChange={(name) => setRow(`${STAGED_ELIGIBILITY}${name}`)}
                    disabled={disabled}
                  />
                ) : (
                  <TdSelect
                    id={`${idPrefix}-eligibility-ids[${index}]`}
                    ariaLabel={`Eligibility, row ${index + 1}`}
                    value={row.eligibilityId}
                    onChange={setRow}
                    options={eligibilityOptions.filter((option) => !taken.has(option.value))}
                    remote
                    /*
                      `allowCustom` with a STAGING handler rather than a bare `allowCustom`,
                      because this picker is over an id — see stageEligibility. Gated on the
                      catalog's key. Nothing is written until the form is submitted.
                    */
                    allowCustom={canCreateEligibility}
                    onCreateOption={canCreateEligibility ? (raw) => stageEligibility(row.key, raw) : undefined}
                    disabled={disabled}
                  />
                )}
              </InlineRow>
            );
          })}
        </InlineAddTable>
      </QualificationLabel>

      <TextLinesTable
        idPrefix={idPrefix}
        field="education"
        label="Education"
        help="One accepted qualification per row, e.g. Bachelor's degree in Information Technology. Any one listed qualifies."
        placeholder="e.g. Bachelor's degree relevant to the job"
        rows={draft.education}
        onRows={(education) => onChange({ education })}
        disabled={disabled}
      />

      <QualificationLabel
        label="Training"
        help="One training per row, with its hours where the standard states them."
      >
        <InlineAddTable
          headers={['Training', { label: 'Hours', align: 'right' }]}
          colWidths={[420, 120]}
          flexColumn={0}
          rowCount={draft.training.length}
          onAddRow={() => onChange({ training: [...draft.training, blankTrainingRow()] })}
          addDisabled={
            disabled ||
            draft.training.some((row) => row.text.trim().length === 0) ||
            draft.training.length >= LINES_MAX
          }
          addDisabledReason={
            draft.training.length >= LINES_MAX
              ? `A standard may list at most ${LINES_MAX} lines.`
              : 'Complete the current row before adding another.'
          }
          addButtonId={`${idPrefix}-training-add`}
        >
          {draft.training.map((row, index) => (
            <InlineRow
              key={row.key}
              idx={index + 1}
              onRemove={() => {
                const next = draft.training.filter((other) => other.key !== row.key);
                onChange({ training: next.length > 0 ? next : [blankTrainingRow()] });
              }}
            >
              <TdInput
                id={`${idPrefix}-training[${index}].text`}
                ariaLabel={`Training, row ${index + 1}`}
                placeholder="e.g. Records management"
                value={row.text}
                onChange={(text) =>
                  onChange({
                    training: draft.training.map((other) => (other.key === row.key ? { ...other, text } : other)),
                  })
                }
                disabled={disabled}
              />
              <TdNumber
                id={`${idPrefix}-training[${index}].hours`}
                ariaLabel={`Hours, row ${index + 1}`}
                value={row.hours}
                allowNull
                integerOnly
                min={1}
                max={9999}
                invalid={trainingRowInvalid(row) ? 'Name the training these hours are for.' : null}
                onChange={(hours) =>
                  onChange({
                    training: draft.training.map((other) => (other.key === row.key ? { ...other, hours } : other)),
                  })
                }
                disabled={disabled}
              />
            </InlineRow>
          ))}
        </InlineAddTable>
      </QualificationLabel>

      <TextLinesTable
        idPrefix={idPrefix}
        field="experience"
        label="Experience"
        help="One accepted experience per row, e.g. One year of relevant experience."
        placeholder="e.g. One year of relevant experience"
        rows={draft.experience}
        onRows={(experience) => onChange({ experience })}
        disabled={disabled}
      />

      {/*
        Plain text, not a list: a note the reader types and moves on from. The ceiling is the
        one `<Textarea>` requires, so a long answer cannot push the confirm tick out of reach.
      */}
      <Textarea
        id={`${idPrefix}-other-expectations`}
        label="Other expectations"
        description="Anything else this title asks of whoever holds it that the standards above do not name."
        value={draft.otherExpectations}
        onChange={(e) => onChange({ otherExpectations: e.target.value })}
        maxLength={2000}
        minRows={3}
        maxRows={8}
        disabled={disabled}
      />
    </>
  );
}

/**
 * A required inline table's label line — the label, its asterisk and the HelpTip, with the
 * sentence in the DOM `sr-only` as well. The plantilla form's Slots grid uses the same pattern:
 * a table has no label of its own, and every shared input's label line looks like this.
 */
function QualificationLabel({
  label,
  help,
  required = true,
  children,
}: {
  label: string;
  help: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="flex min-h-5 items-center gap-1.5 pb-1.5">
        <p className="text-sm text-slate-700">
          {label}
          {required ? (
            <span className="ml-0.5 text-red-600" aria-hidden="true">
              *
            </span>
          ) : null}
        </p>
        <HelpTip label={help} />
      </div>

      <p className="sr-only">
        {label}{required ? ', required' : ''}. {help}
      </p>

      {children}
    </div>
  );
}

/** One required standard entered as plain-text lines — education and experience. */
function TextLinesTable({
  idPrefix,
  field,
  label,
  help,
  placeholder,
  rows,
  onRows,
  disabled,
}: {
  idPrefix: string;
  field: 'education' | 'experience';
  label: string;
  help: string;
  placeholder: string;
  rows: TextRow[];
  onRows: (rows: TextRow[]) => void;
  disabled: boolean;
}) {
  return (
    <QualificationLabel label={label} help={help}>
      <InlineAddTable
        headers={[label]}
        colWidths={[520]}
        flexColumn={0}
        rowCount={rows.length}
        onAddRow={() => onRows([...rows, blankTextRow()])}
        addDisabled={disabled || rows.some((row) => row.text.trim().length === 0) || rows.length >= LINES_MAX}
        addDisabledReason={
          rows.length >= LINES_MAX
            ? `A standard may list at most ${LINES_MAX} lines.`
            : 'Complete the current row before adding another.'
        }
        addButtonId={`${idPrefix}-${field}-add`}
      >
        {rows.map((row, index) => (
          <InlineRow
            key={row.key}
            idx={index + 1}
            onRemove={() => {
              const next = rows.filter((other) => other.key !== row.key);
              onRows(next.length > 0 ? next : [blankTextRow()]);
            }}
          >
            <TdInput
              id={`${idPrefix}-${field}[${index}]`}
              ariaLabel={`${label}, row ${index + 1}`}
              placeholder={placeholder}
              value={row.text}
              onChange={(text) => onRows(rows.map((other) => (other.key === row.key ? { ...other, text } : other)))}
              disabled={disabled}
            />
          </InlineRow>
        ))}
      </InlineAddTable>
    </QualificationLabel>
  );
}
